<template>
  <div :class="['image-gallery', { 'image-gallery--grid': !enableSwiperView, 'image-gallery--single-image': imageGallery.length === 1 }]">
    <!-- Swiper layout -->
    <div
      v-if="enableSwiperView"
      ref="gallerySwiper"
      :class="['image-gallery__carousel', 'swiper-container', { 'compress': imageGallery.length > 1 }]"
    >
      <span
        v-if="showSlidesCount && imageGallery.length > 1"
        :class="['image-gallery__count', { 'image-gallery__count-single-image': imageGallery.length === 1 }]"
      >
        {{ currentSlide + 1 }} of {{ imageGallery.length }}
      </span>

      <div class="image-gallery__swiper">
        <div
          v-for="(image, index) in imageGallery"
          :key="index"
          :class="['image-gallery__swiper-slide', { 'image-gallery__swiper-slide-single-image': imageGallery.length === 1 }]"
        >
          <ImageMagnifier
            v-if="!image.link"
            class="image-gallery__image"
            :key="image.src"
            :product-image="image.src"
            :image-index="index"
            :aspect-ratio="isGiftCard ? 1.0352 : 0.806"
            :disable="disableZoom"
          />

          <div v-else class="image-gallery__video-wrapper">
            <iframe
              :src="getVimeoUrl(image.link)"
              class="image-gallery__fit-video"
              width="100%"
              height="100%"
              loading="lazy"
              tabindex="-1"
              aria-hidden="true"
            />
          </div>

          <div v-if="showFitNote(index)" class="image-gallery__media-info" v-html="fitNote" />
        </div>
      </div>

      <div class="image-gallery__nav image-gallery__prev" tabindex="0">
        <svg class="image-gallery__prev-icon">
          <use xlink:href="#icon-prev" />
        </svg>
      </div>
      <div class="image-gallery__nav image-gallery__next" tabindex="0">
        <svg class="image-gallery__next-icon">
          <use xlink:href="#icon-next" />
        </svg>
      </div>
    </div>

    <!-- Grid layout (non-swiper) -->
    <div v-else v-for="(image, index) in imageGallery" :key="index" class="image-gallery__grid-card">
      <ImageMagnifier
        v-if="!image.link"
        class="image-gallery__image"
        :key="image.src"
        :product-image="image.src"
        :image-index="index"
        :aspect-ratio="isGiftCard ? 1.0352 : 0.806"
      />

      <div v-else class="image-gallery__video-wrapper">
        <iframe
          :src="`${image.link}?autoplay=1&loop=1&muted=1`"
          class="image-gallery__fit-video"
          width="100%"
          height="100%"
          loading="lazy"
          tabindex="-1"
          aria-hidden="true"
        />
      </div>

      <div v-if="showFitNote(index)" class="image-gallery__media-info" v-html="fitNote" />
    </div>
  </div>
</template>

<script setup>
  import { computed, toRefs, ref, onMounted, reactive, watch } from 'vue';
  import { useStore } from 'vuex';
  import ZoomImage from '../../global/zoom-image.vue';
  import ImageMagnifier from '../../global/image-magnifier.vue';
  import Swiper from 'swiper';
  import SwiperCore, { Navigation, EffectFade, Thumbs } from 'swiper/core';

  SwiperCore.use([Navigation, EffectFade, Thumbs]);

  const props = defineProps({
    product: {
      type: Object,
      required: true,
    },
    isDesktopScreen: {
      type: Boolean,
      default: false,
    },
    slidesPerView: {
      type: Number,
      default: null,
    },
    showSlidesCount: {
      type: Boolean,
      default: false,
    },
    disableZoom: {
      type: Boolean,
      default: false,
    },
    slidesOffsetAfter: {
      type: Number,
      default: null,
    },
    isGiftCard: {
      type: Boolean,
      default: false,
    }
  });

  const store = useStore();
  const { product, isDesktopScreen, slidesPerView, slidesOffsetAfter } = toRefs(props);
  const productForm = ref(store.state.pdp.productForm);
  const firstOption = reactive(product.value.options[0].name);
  const gallerySwiper = ref(null);
  const currentSlide = ref(0);
  const swiperInstance = ref(null);

  const enableSwiperView = computed(() => !isDesktopScreen.value);

  const imageGallery = computed(() => {
    const { title, images, fit_info } = product.value;
    const { fit_video_url, fit_video_placeholder, fit_video_position } = fit_info || {};
    const { Color, Town } = productForm.value || {};
    let imageData = window.getProductImages(images, Color, title, Town);

    if (fit_video_url) {
      const videoData = {
        src: fit_video_placeholder,
        alt: 'Fit Video',
        link: fit_video_url,
      };

      // Default to position 2 (i.e., index 1) if not provided or invalid
      let position = parseInt(fit_video_position);
      let insertIndex = isNaN(position) || position < 1 ? 1 : position - 1;

      // Clamp index to the length of imageData
      if (insertIndex > imageData.length) {
        insertIndex = imageData.length;
      }

      imageData.splice(insertIndex, 0, videoData);
    }

    return imageData;
  });

  const fitNote = computed(() => {
    return product.value.variants.find((prod) => prod.fit_note && prod['option1'] === productForm.value[firstOption])
      ?.fit_note;
  });

  const galleryCarousel = computed(() => {
    return {
      slidesPerView: slidesPerView.value ?? (enableSwiperView.value && imageGallery.value.length > 1 ? 1.3 : 1),
      spaceBetween: 8,
      slidesOffsetAfter: slidesOffsetAfter.value ?? imageGallery.value.length > 1 ? 16 : 0,
      wrapperClass: 'image-gallery__swiper',
      slideClass: 'image-gallery__swiper-slide',
      navigation: {
        nextEl: '.image-gallery__next',
        prevEl: '.image-gallery__prev',
      },
      a11y: {
        enabled: true,
        prevSlideMessage: `Go to previous carousel slide of the product ${product.value.title}`,
        nextSlideMessage: `Go to next carousel slide of product ${product.value.title}`,
        itemRoleDescriptionMessage: `This is a product carousel for one of the variant of ${product.value.title}`,
      },
      on: {
        slideChange: function () {
          currentSlide.value = this.realIndex;
        },
      },
    };
  });

  onMounted(() => {
    if (enableSwiperView.value) {
      initializeSwipers();
    }
  });

  const initializeSwipers = () => {
    swiperInstance.value = new Swiper(gallerySwiper.value, galleryCarousel.value);
    currentSlide.value = swiperInstance.value.realIndex;
  };

  const showFitNote = (index) => {
    if (fitNote.value) {
      return imageGallery.value.length > 1 ? index === 1 : index === 0;
    }

    return false;
  };

  const getVimeoUrl = (url) => {
    const videoId = url.split('/').pop(); // extract ID from original URL
    const baseUrl = `https://player.vimeo.com/video/${videoId}`;
    
    const params = new URLSearchParams({
      autoplay: 1,
      loop: 1,
      muted: 1,
      api: 1,
      controls: isDesktopScreen.value ? 1 : 0,
      title: 0,
      byline: 0,
      portrait: 0,
    });

    return `${baseUrl}?${params.toString()}`;
  };

  watch(imageGallery, () => {
    if (enableSwiperView.value) {
      setTimeout(() => {
        swiperInstance.value.destroy();
        initializeSwipers();
      }, 100);
    }
  });
</script>
